import './Utility/WebSocketCommunication';
import { AD_COOLDOWN_TIME } from 'Constants/Ads';
import { petDayIndex } from 'Constants/DailyLogin';
import { PetId } from 'Constants/Pets';
import { INITIAL_PREMIUM_SPINS_AMOUNT } from 'Constants/Player';
import { questSlotsCount, questTimerDuration, questUnlockLevel } from 'Constants/Quests';
import IPlayerData, { playerDataKeysToLoad } from 'Player/IPlayerData';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { MyPetMiniGameSceneParams } from 'Scene/MyPetMiniGameScene';
import { AnyBoosterType, BoosterType, PreBoosterType } from 'State/Card/BoosterType';
import { FarmActions } from 'State/Farm/FarmActions';
import { scheduleInactivityNotification } from 'State/Game/Action/ScheduleNotification';
import { AdDebugMode } from 'State/Game/AdDebugMode';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { GameActions } from 'State/Game/GameActions';
import { farmTransitionSceneId, GameScene, plinkoTransitionSceneId } from 'State/Game/GameScene';
import { IMessagePayload } from 'State/Game/IEntryPointData';
import { IGameState } from 'State/Game/IGameState';
import { setForceHelperFriend } from 'State/Level/HelperFriend/HelperFriendStateHelper';
import { initialFarmPlayerState } from 'State/Player/Farm/initialFarmPlayerState';
import { initialPlayerState } from 'State/Player/initialPlayerStates';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { IPlayerState } from 'State/Player/IPlayerState';
import { initialPetsState } from 'State/Player/Pets/initialPetsState';
import { PetsStateHelper } from 'State/Player/Pets/PetsStateHelper';
import { PlayerActions } from 'State/Player/PlayerActions';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';
import { PlayerTask } from 'State/Player/PlayerTasks';
import { IPlayerLevelData, LevelType } from 'State/Player/Progress/PlayerLevels';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { QuestStateHelper } from 'State/Player/Quests/QuestStateHelper';
import { QuestType } from 'State/Player/QuestType';
import { StarChestStateHelper } from 'State/Player/StarChests/StarChestStateHelper';
import { TimerActions } from 'State/Timer/TimerActions';
import { PopupID } from 'State/UI/PopupID';
import { UIActions } from 'State/UI/UIActions';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { StarChestUpgrade } from 'System/Animation/Clip';
import { MovePet } from 'System/Animation/Clip/MovePet';
import { getUnlockedFields } from 'System/Field/FieldHelper';
import { getPlinkoConfig } from 'System/Plinko/Helpers';
import { TimerType } from 'System/Timer/TimerTypes';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { decryptPayload, encryptPayload } from 'Utility/EntryPoint/EntryPointEncryption';
import { EntryPoint } from 'Utility/EntryPoint/EntryPointHelper';
import { PetAnimations } from 'Utility/Helpers/Pets/PetAnimations';
import { PetsHelper } from 'Utility/Helpers/Pets/PetsHelper';
import { ILimit, MathHelper } from 'Utility/MathHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { GBCHandler } from 'Utility/SDK/GBCHandler';
import PayloadHelper from 'Utility/SDK/PayloadHelper';
import { StarChestHelper } from 'Utility/StarChestHelper';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import Game from './Game';
import TimerStateHelper from './State/Timer/TimerStateHelper';
import WorldSystem from './System/World/WorldSystem';
import DailyLoginHelper from './Utility/DailyLoginHelper';
import DateHelper, { setServerDate } from './Utility/DateHelper';
import Point from './Utility/Point';
import QuestHelper from './Utility/QuestHelper';
import AnimationTask from './Utility/Task/AnimationTask';
import { FieldStateHelper } from 'State/Player/Fields/FieldStateHelper';
import { FARM_UNLOCK_LEVEL } from 'Constants/Farm';
import { unlockLevelByPreBoosterType } from 'System/UI/Menu/LevelPopup/LevelPopupBooster';
import { PlaySound, StopSound } from 'System/Audio/AudioHelper';
import { A2U } from 'Utility/A2U/src/A2U';
import { A2UNotification, A2UNotificationStatus } from 'Utility/A2U/src/types';
import { IAPDebugMode } from 'State/Game/IAPDebugMode';
import { ProductId } from 'Utility/Product';
import { Logger } from 'Utility/Logger/Logger';
import { LogOrigin } from 'Utility/Logger/LogOrigin';
import { LogStyles } from 'Utility/Logger/LogStyles';
import { addToPurchaseHistory, fillWith, removeFromPurchaseHistory, resetPurchasedProducts } from 'CheatsHelper';
import { getHighestUniversalValue, getTotalRealCurrencySpent } from 'State/Player/IAP/IAPStateHelper';
import LevelStateHelper from 'State/Level/LevelStateHelper';
import { LanguageKey } from 'Utility/SDK/Language';
import CheatsManager from 'CheatsManager';
import { increaseSeasonPassProgress, unlockPremiumPass } from 'State/Player/PlayerSeasonPassHelper';
import { SeasonPassActions } from 'State/SeasonPass/SeasonPassActions';
import { isArray } from 'lodash';
import { personaEnabled, setSimulationAttempts, simulateLevel } from 'Utility/Simulator';

declare const FBInstant: any;

export let cheatsUsed: boolean = false;
export const XSESSION_DATE_KEY = 'xsession_date';

export const cheats = {
    clearPlayerData: () => {
        // tslint:disable-next-line:no-console
        console.log('Clearing player data - please wait...');
        Game.sdk.save = async () => void 0;

        Game.sdk.clearPlayerData().then(() => {
            // tslint:disable-next-line:no-console
            console.log('Player data cleared. Please restart the game.');
            setTimeout(() => location.reload(), 500);
        });
    },
    fetchFBData: (keys: Array<string> = playerDataKeysToLoad) => {
        return FBInstant.player.getDataAsync(keys);
    },
    fetchGBCData: () => {
        return GBCHandler.debugLoadFromGBC();
    },
    pauseHarvestTimer: (paused: boolean) => {
        Game.settings.harvestPaused = paused;
        return `Harvest timer has ${paused ? 'paused' : 'resumed'}`;
    },
    resetFriendData: () => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            social: {
                ...playerState.social,
                friendData: [],
            },
        });
    },
    resetFTUX: (unlock?: FTUXType) => {
        PlayerActions.clearFTUX(unlock);
    },
    resetFBEvent: () => {
        Game.sdk.debugResetFBEvents();
    },
    resetFields: (field?: string, reload: boolean = true) => {
        PlayerActions.clearField(field);
        if (reload) {
            GameActions.loadWorld();
        }
    },
    resetTimers: (reload: boolean = true) => {
        const playerState = Game.store.getState().playerState;
        const emptyTimerMap: Map<string, Date> = new Map<string, Date>();

        playerState.timers.forEach((date: Date, key: string) => emptyTimerMap.set(key, null));

        PlayerActions.setPlayerState({
            ...playerState,
            timers: emptyTimerMap,
            social: {
                ...playerState.social,
                friendData: Game.store.getState().playerState.social.friendData.map(friend => ({
                    ...friend,
                    currencySendTimer: null,
                    currencyRequestTimer: null,
                    giftCooldown: null,
                    helperTimer: null,
                })),
            },
        });

        if (reload) {
            GameActions.loadWorld();
        }
    },
    resetFarmMiniGame: (reload: boolean = true) => {
        TimerActions.updateTimer({
            timer: TimerType.FARM_MINI_GAME,
            time: null,
        });

        if (reload) {
            GameActions.loadFarmScene({ loadingSceneId: farmTransitionSceneId });
        }
    },
    setCoins: (value: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState(InventoryStateHelper.setCoins(playerState, value));
    },
    setLevel: (level: number, stars: number = 3, levelType: LevelType = LevelType.Core) => {
        stars = Math.max(Math.min(stars, 3), 0);

        const levelData: IPlayerLevelData[] = [];
        for (let i = 1; i <= level; i++) {
            levelData.push({ levelNumber: i, stars: stars });
        }
        // Clear unlocked fields
        let state: IGameState = Game.store.getState();
        state = FieldStateHelper.clearField(state);
        state = FTUXStateHelper.clearFTUXProgress(state, FTUXType.FARM, FTUXType.QUESTS, FTUXType.FARM_BTN);

        let playerState: IPlayerState = state.playerState;

        // Reset star chest if cheating past the current star chest
        if (level > playerState.starChestLevel) {
            playerState = StarChestStateHelper.setStarChestLevel(state.playerState, null);
        }

        let worldLimits: ILimit = WorldDataHelper.getWorldLevelLimits(playerState.maxWorld);
        while (!MathHelper.isWithinRange(level, worldLimits.min, worldLimits.max)) {
            if (level > worldLimits.max) {
                playerState = ProgressStateHelper.unlockNextWorld(playerState, true);
            } else if (level < worldLimits.min) {
                playerState = {
                    ...playerState,
                    maxWorld: playerState.maxWorld - 1,
                };
            }
            worldLimits = WorldDataHelper.getWorldLevelLimits(playerState.maxWorld);
        }
        if (!FTUXStateHelper.isFtuxCompleted(playerState, FTUXType.HARVEST) && level > 2) {
            playerState = FTUXStateHelper.updateFTUXProgress(playerState, [FTUXType.HARVEST]);
        }

        // get all unlocked fields
        const fields = getUnlockedFields(level);

        // get ftuxs to complete
        const completedFTUXs = getCompletedFTUXs();

        // Unlock pre level boosters
        const unlockedBoosters = getUnlockedPreLevelBoosters();

        PlayerActions.setPlayerState({
            ...playerState,
            levels: {
                ...playerState.levels,
                [levelType]: levelData,
            },
            fields,
            FTUX: [...playerState.FTUX, ...completedFTUXs],
            unlockedBoosters,
        });

        GameActions.loadWorld({ firstComplete: true });
        return 'Level Set. Reloading World..';

        function getUnlockedPreLevelBoosters(): AnyBoosterType[] {
            return Object.keys(unlockLevelByPreBoosterType).filter(
                (key: PreBoosterType) =>
                    unlockLevelByPreBoosterType.hasOwnProperty(key) && level >= unlockLevelByPreBoosterType[key]
            ) as PreBoosterType[];
        }

        function getCompletedFTUXs() {
            const completedFTUXs = [];
            if (level >= FARM_UNLOCK_LEVEL) {
                completedFTUXs.push(FTUXType.FARM, FTUXType.FARM_BTN);
            }
            if (level >= questUnlockLevel) {
                completedFTUXs.push(FTUXType.QUESTS);
            }
            return completedFTUXs;
        }
    },
    completeLevel: () => {
        GameActions.setState(LevelStateHelper.handleSuccessfulLevelComplete(Game.store.getState()));
    },
    setWorld: (maxWorld: number) => {
        let state: IGameState = Game.store.getState();
        let levelLimit: keyof ILimit = 'min';
        if (maxWorld > state.playerState.maxWorld) {
            while (state.playerState.maxWorld < maxWorld) {
                state.playerState = ProgressStateHelper.unlockNextWorld(state.playerState);
            }
            levelLimit = 'min';
        } else {
            state.worldState.worldNumber = maxWorld;
            state.playerState.maxWorld = maxWorld;
            levelLimit = 'max';
        }
        GameActions.setState(state);

        const playerLevel: number = Game.player.getHighestLevelCompleted();
        const worldLimits: ILimit = WorldDataHelper.getWorldLevelLimits(state.playerState.maxWorld);
        if (!MathHelper.isWithinRange(playerLevel, worldLimits.min, worldLimits.max)) {
            GameCheats.setLevel(worldLimits[levelLimit] - 1);
        }
        GameActions.loadWorld({
            worldNumber: maxWorld,
            firstComplete: levelLimit === 'max',
        });
    },
    setBoosterTNT: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterTNT(amount - playerState.inventory[BoosterType.TNT]);
    },
    setBoosterBullsEye: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterBullsEye(amount - playerState.inventory[BoosterType.BULLS_EYE]);
    },
    setBoosterShuffle: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterShuffle(amount - playerState.inventory[BoosterType.SHUFFLE]);
    },
    setBoosterUndo: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterUndo(amount - playerState.inventory[BoosterType.UNDO]);
    },
    setBoosterJoker: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterJoker(amount - playerState.inventory[BoosterType.JOKER]);
    },
    setPreBoosterCanister: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [PreBoosterType.CANISTER]: amount,
            },
        });
    },
    setPreBoosterJoker: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [PreBoosterType.JOKER]: amount,
            },
        });
    },
    setPreBoosterRemoveThree: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [PreBoosterType.REMOVE_THREE]: amount,
            },
        });
    },
    setFreePlay: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [RewardType.FREE_PLAY]: amount,
            },
        });
    },
    setShopGift: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                shopGift: amount,
            },
        });
    },
    setAdsWatched: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            progress: {
                ...playerState.progress,
                adsWatched: amount,
            },
        });
    },
    setBoosterPlusFive: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.debugReceiveBoosterPlusFive(amount - playerState.inventory[BoosterType.PLUS_FIVE]);
        console.log('playerState', playerState);
    },
    clearPreBoosters: () => {
        const playerState = Game.store.getState().playerState;
        playerState.inventory[PreBoosterType.CANISTER] = 0;
        playerState.inventory[PreBoosterType.JOKER] = 0;
        playerState.inventory[PreBoosterType.REMOVE_THREE] = 0;
        PlayerActions.setPlayerState(playerState);
        console.log('playerState', playerState);
    },
    setExtraCards: (value: number) => {
        PlayerActions.receiveRewards({
            rewards: [
                {
                    type: RewardType.CARDS,
                    value: value,
                },
            ],
        });
    },
    receiveReward: (type: RewardType, value: any) => {
        PlayerActions.receiveRewards({
            rewards: [
                {
                    type,
                    value,
                },
            ],
        });
    },
    unlockBackcard: (backcardId?: number) => {
        PlayerActions.unlockBackCard(backcardId);
    },
    setQuestProgress: (questNumber: number, progress: number) => {
        PlayerActions.setPlayerState(
            QuestStateHelper.setQuestProgress(Game.store.getState().playerState, questNumber - 1, progress)
        );
    },
    setQuestProgressByType: (questType: QuestType, progress: number) => {
        PlayerActions.setPlayerState(
            QuestStateHelper.incrementQuestProgressBy(Game.store.getState().playerState, questType, progress)
        );
    },
    setWheelSpins: (spins: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveWheelSpins(spins - playerState.inventory.wheelSpins);
    },
    setPremiumWheelSpins: (spins: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [RewardType.PREMIUM_SPIN]: spins,
            },
        });
        GameActions.loadWorld();
    },
    resetSpunFirstWheels: () => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            spunFirstNormalWheel: false,
            spunFirstDiamondWheel: false,
        });
        GameActions.loadWorld();
    },
    cinematicMode: (value: boolean = true) => {
        const state: IGameState = Game.store.getState();

        PlayerActions.setPlayerState({
            ...state.playerState,
            settings: {
                ...state.playerState.settings,
                cinematicMode: value,
            },
        });

        GameActions.loadWorld({
            worldNumber: state.worldState.worldNumber,
            firstComplete: true,
        });
    },
    showState: () => {
        console.log(Game.store.getState());
    },
    setBoosterReward: (boosterType: BoosterType, value: number) => {
        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        worldSystem.taskQueue.queue(
            new AnimationTask('RewardBoosters', worldSystem.animator, {
                type: boosterType,
                amount: value,
                node: worldSystem.getCurrentLevelNode(),
            })
        );
        worldSystem.taskQueue.start(worldSystem);
    },
    setBoosterCoins: (type: PlayerTask, value: number) => {
        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        worldSystem.taskQueue.queue(
            new AnimationTask(
                'RewardCoins',
                worldSystem.animator,
                {
                    scene: worldSystem.scene,
                    type,
                    value,
                    previousValue: Game.store.getState().playerState.inventory.coins,
                    texture: 'coin_large',
                    increment: 100,
                },
                false
            )
        );
        worldSystem.taskQueue.start(worldSystem);
    },
    setInitialLogin: (days: number) => {
        let state: IGameState = Game.store.getState();
        const newDate: Date = new Date(new Date(DateHelper.now.setDate(DateHelper.now.getDate() - days)));
        state = TimerStateHelper.setTimer(state, TimerType.INITIAL_LOGIN, newDate);
        state = TimerStateHelper.setTimer(state, TimerType.LAST_LOGIN, DateHelper.yesterday);

        PlayerActions.setPlayerState({
            ...state.playerState,
        });

        return console.warn('Initial Login Date changed to: ' + newDate);
    },
    setPlayerPurchases: (purchases: number, reload: boolean = false) => {
        const state: IGameState = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            purchases,
        });

        if (reload) {
            GameActions.loadWorld({ worldNumber: state.worldState.worldNumber });
        }
    },
    setDifficultyReduction: (difficultyReduction: number) => {
        const state = PlayerStateHelper.setDifficultyReduction(Game.store.getState(), difficultyReduction);
        PlayerActions.setPlayerState(state.playerState);
    },
    showPopup: (popupName: any, params: any) => {
        UIActions.showPopup(popupName, params);
    },
    showHardLevelShare: () => {
        const level = Game.store.getState().levelState.levelNumber;
        UIActions.showPopup(PopupID.HARD_LEVEL_SHARE_POPUP, { levelNumber: level, firstComplete: true });
    },
    setStarChestStarCount: (count: number) => {
        let playerState = Game.store.getState().playerState;
        const newInitialStarCount = ProgressStateHelper.getStarsCount(playerState, LevelType.Core) - count;
        playerState = StarChestStateHelper.setStarChestInitialStars(playerState, newInitialStarCount);

        PlayerActions.setPlayerState(playerState);

        const tier = StarChestStateHelper.getStarChestUnlockTier(playerState);

        GameCheats.upgradeStarChest(tier);
    },
    setStarChestLevel: (levelNumber: number | null, reload: boolean = true) => {
        const playerState = StarChestStateHelper.setStarChestLevel(Game.store.getState().playerState, levelNumber);

        PlayerActions.setPlayerState(playerState);

        if (reload) {
            GameActions.loadWorld();
        }
    },
    upgradeStarChest: (tier: number) => {
        if (!StarChestHelper.tierExists(tier)) {
            console.error(`Star Chest tier '${tier}' does not exist`);
            return;
        }

        const state = Game.store.getState();
        if (state.playerState.starChestLevel === null) {
            console.error('No star chest currently exists');
            return;
        }

        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        if (!worldSystem) {
            console.error('This command only works from World Map');
            return;
        }

        const levelNode = worldSystem.levelNodes.get(state.playerState.starChestLevel);
        const starChest = levelNode.getChildByName('star_chest');
        if (starChest) {
            StartAnimationClip(
                new StarChestUpgrade(starChest, {
                    tier: tier,
                    updateTexture: false,
                })
            );
        } else {
            console.error(`Star chest not found`);
        }
    },
    playStarChestStarsRewardAnimation: (amount: number, levelNumber?: number) => {
        UIActions.hidePopup(PopupID.LEVEL_POPUP);
        const state = Game.store.getState();
        const previousStarCount: number = StarChestStateHelper.getStarChestStarCount(state.playerState);

        const playerState = PlayerStateHelper.addStateTaskDatas(state.playerState, [
            {
                task: PlayerTask.STAR_CHEST_STAR_REWARD,
                amount,
                previousStarCount,
                currentStarChestTier: StarChestStateHelper.getStarChestUnlockTier(state.playerState),
                levelNumber,
            },
        ]);
        PlayerActions.setPlayerState(playerState);
    },
    playStarChestFallAnimation: () => {
        UIActions.hidePopup(PopupID.LEVEL_POPUP);

        const state = Game.store.getState();
        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        const levelNode = worldSystem.getLevelNode(state.playerState.starChestLevel);
        const starChest = levelNode.findChildByName('star_chest');
        worldSystem.taskQueue.queue(new AnimationTask('StarChestFall', starChest, {}));
        worldSystem.taskQueue.start(worldSystem);
    },
    playStarChestFTUX: () => {
        UIActions.hidePopup(PopupID.LEVEL_POPUP);
        const state = Game.store.getState();

        const playerState = PlayerStateHelper.addStateTaskDatas(state.playerState, [
            {
                task: PlayerTask.STAR_CHEST_FTUX,
                starChestLevel: state.playerState.starChestLevel,
            },
        ]);
        PlayerActions.setPlayerState(playerState);
    },
    setMissionProgress: (questIndex: number, progress: number) => {
        const state = Game.store.getState();
        const quest = state.playerState.quests[questIndex];
        let playerState;
        if (progress > quest.progress) {
            playerState = QuestStateHelper.incrementQuestProgressBy(
                state.playerState,
                quest.type,
                progress - quest.progress
            );
        } else {
            playerState = QuestStateHelper.setQuestProgress(state.playerState, questIndex, progress);
        }
        PlayerActions.setPlayerState(playerState);
    },
    setMissionComplete: (questIndex: number) => {
        const state = Game.store.getState();
        const quest = state.playerState.quests[questIndex];
        const questTarget = QuestHelper.getQuestTarget(
            quest.type,
            quest.tier,
            QuestHelper.getQuestPoolKeyByEventType(quest.eventType)
        );
        cheats.setMissionProgress(questIndex, isArray(questTarget) ? questTarget[0] : questTarget);
    },
    setMissionType: (questIndex: number, questType: QuestType, tier?: number) => {
        const state = Game.store.getState();
        let playerState = {
            ...state.playerState,
            quests: ArrayHelper.changeElement(state.playerState.quests, questIndex, () => {
                return QuestHelper.getNewQuest({
                    currentQuests: state.playerState.quests,
                    forcedQuestType: questType,
                    forcedTier: tier,
                });
            }),
        };
        PlayerActions.setPlayerState(playerState);
    },
    claimMissionReward: (questIndex: number) => {
        PlayerActions.claimQuestReward(questIndex);
    },
    completeMissionTimer: (questIndex?: number) => {
        const state = Game.store.getState();
        let playerState = { ...state.playerState };

        if (questIndex === undefined) {
            for (let i = 0; i < questSlotsCount; i++) {
                refreshQuest(i);
            }
        } else {
            refreshQuest(questIndex);
        }

        return;
        function refreshQuest(index: number) {
            PlayerActions.setPlayerState(
                QuestStateHelper.refreshQuestAfterCooldown({ ...state, playerState }, index).playerState
            );
        }
    },
    setLoginStreak: (value: number) => {
        const clampedValue = MathHelper.clamp(value, 1, Infinity);
        if (clampedValue !== value) {
            console.warn(`Invalid streak length provided, defaulting to: ${clampedValue}`);
        }
        const playerState = Game.store.getState().playerState;
        const { todayClaimWindowEnd, lastClaimWindowEnd } = DailyLoginHelper.getClaimWindows();

        PlayerActions.setPlayerState({
            ...playerState,
            progress: {
                ...playerState.progress,
                // Filter out any rewards that were claimed today to allow a new reward to be collected
                dailyLoginRewardsClaimed: playerState.progress.dailyLoginRewardsClaimed.filter(
                    reward => !DailyLoginHelper.wasRewardClaimedWithin(reward, lastClaimWindowEnd, todayClaimWindowEnd)
                ),
            },
        });
        PlayerActions.updateLoginStreak(value);
    },
    clearLastSundayPurchase: () => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            lastSundayOfferPurchaseTimestamp: 0,
        });
    },
    setDay: (day: number) => {
        day = MathHelper.clamp(day, 0, 6);
        while (DateHelper.now.getDay() !== day) {
            setServerDate(DateHelper.now.getTime() - DateHelper.Day);
        }
        GameActions.loadWorld();
    },
    scheduleInactiveNotification() {
        scheduleInactivityNotification(Game.store.getState(), true);
    },
    setPetLevel: (value: number, reload: boolean = true) => {
        PlayerActions.setPetLevel(value);
        if (reload) {
            GameActions.loadWorld();
        }
    },
    setActivePet: (value: PetId, reload: boolean = true) => {
        let playerState = Game.store.getState().playerState;
        if (!PetsStateHelper.isPetUnlocked(playerState.petsState, value)) {
            playerState = PetsStateHelper.updatePlayerState(
                playerState,
                PetsStateHelper.unlockPet(playerState.petsState, value)
            );
        }
        playerState = PetsStateHelper.updatePlayerState(
            playerState,
            PetsStateHelper.setActivePet(playerState.petsState, value)
        );

        PlayerActions.setPlayerState(playerState);
        if (reload) {
            GameActions.loadWorld();
        }
    },
    clearPetData: (value: boolean = false, reload: boolean = true) => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...FTUXStateHelper.clearFTUXProgress(state, FTUXType.PET_UNLOCK).playerState,
            petsState: { ...initialPetsState, petInDailyLogin: value },
            petLevel: null,
        });
        if (value) {
            cheats.setLoginStreak(petDayIndex);
        }
        if (reload) {
            GameActions.loadWorld();
        }
    },
    encryptPayload: (payload: EntryPoint) => {
        return encryptPayload(payload);
    },
    decryptPayload: (cipherText: string) => {
        return decryptPayload(cipherText);
    },
    movePet: (nodesToMove: number, reload: boolean) => {
        const system = Game.scene.getActiveScene().getSystem(WorldSystem);
        const state = Game.store.getState();
        const petLevel = state.playerState.petLevel;
        const petData = PetsHelper.getPetData(state.playerState.petsState.activePet);

        if (petLevel === null) return;

        const pet = system.pet;

        pet.initialized
            .then(() => {
                const system = Game.scene.getActiveScene().getSystem(WorldSystem);
                const points: Point[] = [];

                for (let i = petLevel + 1; i <= petLevel + nodesToMove; i++) {
                    points.push(getNodePosition(i));
                }

                StartAnimationClip(
                    new MovePet(pet, {
                        points,
                        exitAnimations: [PetAnimations.WALKING_TO_IDLE, PetAnimations.IDLE],
                    })
                );
                return;
                function getNodePosition(levelNumber: number): Point {
                    return Point.add(system?.getLevelNode(levelNumber).getPetPosition(), petData.nodeWalkOffset);
                }
            })
            .catch(error => console.error(error));
    },
    startPetMiniGame: (props: { forceJackpotWin?: boolean } = {}) => {
        const params: MyPetMiniGameSceneParams = { forceJackpotWin: props.forceJackpotWin };
        GameActions.loadMyPetMiniGame(params);
    },
    setServerTime: (time: number) => {
        setServerDate(time);
        GameActions.loadWorld();
    },
    setServerDate: (
        year: number,
        month: number,
        day: number,
        hours: number = 0,
        minutes: number = 0,
        seconds: number = 0
    ) => {
        const date = new Date(year, month - 1, day, hours, minutes, seconds);
        setServerDate(date.getTime());
        GameActions.loadWorld();
    },
    addDays: (days: number) => {
        setServerDate(DateHelper.now.getTime() + DateHelper.daysToMilliseconds(days));
        GameActions.loadWorld();
    },
    addHours: (hours: number) => {
        setServerDate(DateHelper.now.getTime() + DateHelper.hoursToMilliseconds(hours));
        GameActions.loadWorld();
    },
    addMinutes: (minutes: number) => {
        setServerDate(DateHelper.now.getTime() + DateHelper.minutesToMilliseconds(minutes));
        GameActions.loadWorld();
    },
    setMarketingCampaign: (campaign: string) => {
        let playerState = Game.store.getState().playerState;
        playerState = {
            ...playerState,
            attributionCampaignID: campaign || initialPlayerState.attributionCampaignID,
        };
        PlayerActions.setPlayerState(playerState);
    },
    getMarketingCampaign: () => {
        return Game.store.getState().playerState.attributionCampaignID;
    },
    debugXPromo: () => {
        const state = Game.store.getState();
        GameActions.setState({
            ...state,
            perSessionData: {
                ...state.perSessionData,
                debugXPromo: true,
            },
        });
    },
    showFriendHelper: (value: boolean = true) => {
        setForceHelperFriend(value);
    },
    resetLastClaimedSpinLevel: () => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            lastClaimedSpinLevel: 0,
            premiumWheelSpinsAmount: Math.max(0, INITIAL_PREMIUM_SPINS_AMOUNT),
        });
        GameActions.loadWorld();
    },
    setRemainingPremiumSpins: (value: number) => {
        PlayerActions.setPlayerState({
            ...Game.store.getState().playerState,
            premiumWheelSpinsAmount: Math.max(0, value),
        });
    },
    sendCustomUpdate: (data: {
        friendId: string;
        payload?: IMessagePayload;
        text?: string;
        imageBase64?: string;
        strategy?: string;
        cta?: string;
    }) => {
        let {
            text = 'Custom Update',
            imageBase64 = PayloadHelper.getScreenshotBase64(),
            payload = {},
            friendId,
            strategy,
            cta,
        } = data;

        Game.sdk.customUpdate(text, imageBase64, friendId, payload, strategy, cta);
    },
    setTimer: (timerType: TimerType, date: Date | null) => {
        Game.store.dispatch(
            TimerActions.updateTimer({
                timer: timerType,
                time: date,
            })
        );
    },
    clearFarm: () => {
        PlayerActions.setPlayerState({
            ...Game.store.getState().playerState,
            farmState: initialFarmPlayerState,
        });
    },
    clearFarmAreas: () => {
        FarmActions.clearFarmAreas();
        if (Game.store.getState().activeScene.result === GameScene.FARM) {
            GameActions.loadFarmScene({ loadingSceneId: farmTransitionSceneId });
        } else {
            GameActions.loadWorld();
        }
    },
    setPlayerState: (playerState: IPlayerState) => {
        PlayerActions.setPlayerState(playerState);
    },
    updatePlayerState: (playerState: Partial<IPlayerState>) => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            ...playerState,
        });
    },
    clearEventQuests: () => {
        PlayerActions.setPlayerState({
            ...Game.store.getState().playerState,
            eventQuests: [],
        });
    },
    clearSeenDecorations: () => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            decoItemsSeen: [],
        });
    },
    clearDecoItemsRewarded: () => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            decoItemsRewarded: [],
        });
    },
    setUAMissionAttempts: (attempts: number) => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            missionAttempts: attempts,
        });
    },
    clearUAMissionAttemptsStartDate: () => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            missionAttemptsStartTimestamp: null,
        });
    },
    clearPlusFiveBoosterUsages: () => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            plusFiveBoostersUsed: 0,
        });
    },
    clearPurchase(...productIds: string[]) {
        const state = Game.store.getState();
        let productsPurchased = state.playerState.productsPurchased;

        productIds.forEach(productId => {
            productsPurchased = {
                ...state.playerState.productsPurchased,
                [productId]: 0,
            };
        });

        PlayerActions.setPlayerState({
            ...state.playerState,
            productsPurchased,
        });
    },
    setProductPurchased(productId: string) {
        const state = Game.store.getState();
        let productsPurchased = state.playerState.productsPurchased;
        productsPurchased = {
            ...state.playerState.productsPurchased,
            //@ts-ignore
            [productId]: (state.playerState.productsPurchased[productId] || 0) + 1,
        };

        PlayerActions.setPlayerState({
            ...state.playerState,
            productsPurchased,
        });
    },
    setAdCooldown: (seconds: number) => {
        let state = Game.store.getState();

        state = TimerStateHelper.setTimer(
            state,
            TimerType.AD_COOLDOWN,
            new Date(DateHelper.now.getTime() - AD_COOLDOWN_TIME + seconds * 1000)
        );

        PlayerActions.setPlayerState(state.playerState);
    },
    setAdDebugMode: (mode: AdDebugMode) => {
        GameActions.setAdDebugMode(mode);
    },
    setIAPDebugMode: (mode: IAPDebugMode) => {
        GameActions.setIAPDebugMode(mode);
    },
    setPlinkoTokens: (tokens: number) => {
        PlayerActions.setPlayerState(
            InventoryStateHelper.setInventory(Game.store.getState().playerState, RewardType.PLINKO_TOKEN, tokens)
        );
    },
    goToPlinkoMinigame: (forceJackpot: boolean = false) => {
        const config = getPlinkoConfig();
        GameActions.loadPlinkoScene({
            loadingSceneId: plinkoTransitionSceneId,
            purchasableTokensRemaining: config.purchasesAllowed,
            forceJackpot,
        });
    },
    playSound: (clipName: string, delay: number = 0, params?: unknown) => {
        if (delay > 0) {
            setTimeout(() => PlaySound(clipName, params), delay);
        } else {
            PlaySound(clipName, params);
        }
    },
    stopSound: (clipName: string) => {
        StopSound(clipName);
    },
    disconnectFromGBC: () => {
        GBCHandler.onInactivityTimeout();
    },
    getGBCHandler: () => {
        return GBCHandler;
    },
    saveToGBC: (data: Partial<IPlayerData>) => {
        GBCHandler.saveToGBC(data);
    },
    setGamePaused: (paused: boolean = true) => {
        GameActions.setGamePaused(paused);
    },
    scheduleA2UNotifications: async (notifications: A2UNotification[]) => {
        await A2U.scheduleNotifications(notifications);
    },
    clearA2UNotifications: async () => {
        const notifications = await A2U.fetchNotifications();
        const scheduledNotifications = notifications.filter(A2U.isScheduledNotification);
        for (let i = 0; i < scheduledNotifications.length; i++) {
            const notificationToCancel = scheduledNotifications[i];
            await A2U.cancelNotification({
                notificationId: notificationToCancel.notification_id,
                userASID: notificationToCancel.user_ASID,
            });
        }
    },
    fetchA2UNotifications: async (filterType: A2UNotificationStatus = A2UNotificationStatus.SCHEDULED) => {
        const notifications = await A2U.fetchNotifications();
        let filteredNotifications = [];
        switch (filterType) {
            case A2UNotificationStatus.CANCELED:
                filteredNotifications = notifications.filter(A2U.isCanceledNotification);
            case A2UNotificationStatus.SCHEDULED:
                filteredNotifications = notifications.filter(A2U.isScheduledNotification);
            case A2UNotificationStatus.SENT:
                filteredNotifications = notifications.filter(A2U.isSentNotification);
        }

        console.log(filteredNotifications);
    },
    getTime: () => {
        console.log(DateHelper.now);
    },
    addToPurchaseHistory: (productId: ProductId, checkExistance = true) => {
        let newPlayerState: IPlayerState;
        try {
            newPlayerState = addToPurchaseHistory(Game.store.getState(), productId, checkExistance);
        } catch (err) {
            if (err?.message === 'NO_OFFER_IN_THE_CODE') {
                Logger.log(
                    `This productId doesn't exist in the game. If you want to add it anyway then use cheats.addToPurchaseHistory("${productId}", false)`,
                    LogOrigin.CHEATS,
                    LogStyles.WARNING
                );
                return;
            }
            throw err;
        }
        Game.store.dispatch(PlayerActions.setPlayerState(newPlayerState));
        setTimeout(() => cheats.getPurchaseHistory(), 200);
    },
    removeFromPurchaseHistory: (productId: ProductId, amount?: number) => {
        const newPlayerState = removeFromPurchaseHistory(Game.store.getState().playerState, productId, amount);
        Game.store.dispatch(PlayerActions.setPlayerState(newPlayerState));
        setTimeout(() => cheats.getPurchaseHistory(), 200);
    },
    getPurchaseHistory: () => {
        const productIds: ProductId[] = [];
        const purchasedProducts = Game.store.getState().playerState.productsPurchased;
        let minNameColSize = 15;
        for (const productId in purchasedProducts) {
            const amount = Number(purchasedProducts[productId as ProductId]);
            if (amount > 0) {
                productIds.push(productId as ProductId);
                minNameColSize = Math.max(minNameColSize, productId.length);
            }
        }

        const amountPaddingLeft = 2;
        const totalNameLength = minNameColSize + amountPaddingLeft;
        let str = '#  Product ID';
        str = fillWith(str, ' ', String(productIds.length).length, true);
        str = fillWith(str, ' ', totalNameLength) + 'Purchased Times';
        str = str + '\n' + fillWith('', '-', str.length) + '\n';
        productIds.forEach((productId, i) => {
            const num = fillWith(String(i), ' ', String(productIds.length).length, true);
            const productIdStr = fillWith(productId, ' ', totalNameLength);
            str += `${num}: ${productIdStr} = ${purchasedProducts[productId]}\n`;
        });

        console.log(str);
    },
    getTotalPurchaseValue: () => {
        Logger.log(`Total purchase value: ${getTotalRealCurrencySpent(Game.store.getState())}`, LogOrigin.CHEATS, LogStyles.HYPER_VISIBLE);
    },
    setLanguage: (language: LanguageKey) => {
        Game.locale.setLanguage(language);
        GameActions.loadWorld();
    },
    toggleIAPs: (alwaysSucceed: boolean) => {
        if (alwaysSucceed) {
            cheats.setIAPDebugMode(IAPDebugMode.ALWAYS_SUCCEED);
        } else {
            cheats.setIAPDebugMode(IAPDebugMode.DEFAULT);
        }
    },
    toggleIAPFail: (alwaysFail: boolean) => {
        if (alwaysFail) {
            cheats.setIAPDebugMode(IAPDebugMode.ALWAYS_FAIL);
        } else {
            cheats.setIAPDebugMode(IAPDebugMode.DEFAULT);
        }
    },
    getHighestUniversalValue: () => {
        Logger.log(`Highest Universal value: ${getHighestUniversalValue(Game.store.getState())}`, LogOrigin.CHEATS, LogStyles.HYPER_VISIBLE);
    },
    resetIAP: () => {
        Game.store.dispatch(PlayerActions.setPlayerState(resetPurchasedProducts(Game.store.getState().playerState)));
    },
    disableIAP: () => {
        Game.store.dispatch(GameActions.disableIAP());
    },
    toggleAutoMatch: (enabled: boolean) => {
        CheatsManager.getFirstInstance()?.setAutoMatchEnabled(enabled);
    },
    resetFlags: () => {
        Game.store.dispatch(PlayerActions.resetFlags());
    },
    dumpPlayerData: () => {
        Game.sdk.restore().then((data) => {
            console.log(data);
        });
    },
    getXSessionDate(): Date | undefined {
        const dateStr = window.localStorage?.getItem(XSESSION_DATE_KEY) as string | undefined;
        if (!dateStr) return undefined;
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return undefined;
        return date;
    },
    /** Empty string removes the xsession date cheat. */
    setXSessionDate(dateStr: string | ''): void {
        if (!dateStr) {
            window.localStorage?.removeItem(XSESSION_DATE_KEY);
            logInfo('XSession date is unset');
            logInfo('PAGE REFRESH IS REQUIRED!');
            return;
        }
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) {
            logError('The xsession date is invalid.');
            return;
        }
        window.localStorage?.setItem(XSESSION_DATE_KEY, dateStr);
        logInfo(`XSession date is set: ${date.toISOString()}`);
        logInfo('PAGE REFRESH IS REQUIRED!');
    },
    addSeasonPassPoints: (amount: number) => {
        Game.store.dispatch(PlayerActions.setPlayerState(
            increaseSeasonPassProgress(Game.store.getState().playerState, amount)
        ));
    },
    enablePremiumSeasonPass: () => {
        Game.store.dispatch(PlayerActions.setPlayerState(
            unlockPremiumPass(Game.store.getState().playerState)
        ));
    },
    disablePremiumSeasonPass: () => {
        const playerState = Game.store.getState().playerState;
        Game.store.dispatch(PlayerActions.setPlayerState({
            ...playerState,
            seasonPass: {
                ...playerState.seasonPass,
                boughtPremiumPass: false,
            }
        }));
    },
    resetSeasonPass: () => {
        Game.store.dispatch(SeasonPassActions.resetSeasonPass());
    },
    toggleSimulatorPersona: (personaId: string, enabled: boolean): void => {
        personaEnabled(personaId, enabled);
    },
    setSimulatorAttempts: (attempts: number): void => {
        setSimulationAttempts(attempts);
    },
};

type Cheats = typeof cheats;

// Wrap all cheats into another function to detect when they're used
export const GameCheats: Cheats = Object.entries(cheats).reduce(
    (acc, [funcName, func]: [keyof Cheats, Cheats[keyof Cheats]]) => {
        acc[funcName] = function (...args: Array<any>) {
            console.log(`cheat used: ${funcName}`);
            const state = Game.store.getState();
            cheatsUsed = true;
            PlayerActions.setPlayerState({
                ...state.playerState,
                usedCheats: true,
            });
            return func.call(this, ...args);
        };
        return acc;
    },
    {} as Cheats
);

/* DevFeature:start */
let dragging = false;

const colorInactive = '#ccc';
const colorActive = '#333';

const dragOverlay = document.createElement('div');
dragOverlay.style.position = 'absolute';
dragOverlay.style.top = '0';
dragOverlay.style.left = '0';
dragOverlay.style.width = '100%';
dragOverlay.style.height = '100%';
dragOverlay.style.backgroundColor = 'rgba(255,255,255,0.95)';
dragOverlay.style.padding = '2em';
dragOverlay.style.zIndex = '10000';
dragOverlay.style.pointerEvents = 'none';
dragOverlay.style.boxSizing = 'border-box';
dragOverlay.style.fontFamily = 'sans-serif';

const overlayPlay = document.createElement('div');
overlayPlay.style.width = '100%';
overlayPlay.style.height = 'calc(80% - 1em)';
overlayPlay.style.marginBottom = '1em';
overlayPlay.style.border = '0.2em dashed';
overlayPlay.style.borderRadius = '0.5em';
overlayPlay.style.borderColor = colorInactive;
overlayPlay.style.boxSizing = 'border-box';
overlayPlay.style.textAlign = 'center';
overlayPlay.style.alignContent = 'center';
overlayPlay.style.color = colorActive;
overlayPlay.innerText = 'Drop level here to play it';

const overlaySimulate = document.createElement('div');
overlaySimulate.style.width = '100%';
overlaySimulate.style.height = '20%';
overlaySimulate.style.border = '0.2em dashed';
overlaySimulate.style.borderRadius = '0.5em';
overlaySimulate.style.borderColor = colorInactive;
overlaySimulate.style.boxSizing = 'border-box';
overlaySimulate.style.textAlign = 'center';
overlaySimulate.style.alignContent = 'center';
overlaySimulate.style.color = colorActive;
overlaySimulate.innerText = 'Drop level here to simulate it';

dragOverlay.appendChild(overlayPlay);
dragOverlay.appendChild(overlaySimulate);

export function setupDropListener(): void {
    if (!document) {
        console.error('Could not add drop listener. Document is undefined.');
        return;
    }

    let dropAction: 'simulate' | 'play' = 'simulate';

    document.body.addEventListener('dragover', (event) => {
        event.preventDefault();

        if (window.simulating) return;

        if (!dragging) {
            dragging = true;
            document.body.appendChild(dragOverlay);
        }

        const r = event.y / window.innerHeight;
        dropAction = r < 0.75 ? 'play' : 'simulate';
        overlayPlay.style.borderColor = dropAction === 'play' ? colorActive : colorInactive;
        overlaySimulate.style.borderColor = dropAction === 'simulate' ? colorActive : colorInactive;
    });

    document.body.addEventListener('dragleave', (event) => {
        event.preventDefault();

        if (window.simulating) return;

        if (!dragging) return;

        dragging = false;
        document.body.removeChild(dragOverlay);
    });

    document.body.addEventListener('drop', async (event) => {
        if (window.simulating) return;

        dragging = false;
        document.body.removeChild(dragOverlay);

        event.preventDefault();

        const file = event.dataTransfer.files[0];
        if (!file) return;

        const levelData = JSON.parse(await file.text());

        switch (dropAction) {
            case 'play':
                GameActions.loadLevelDebug(levelData);
                break;
            case 'simulate':
                simulateLevel(levelData);
                break;
        }
    });
}
/* DevFeature:end */

export function checkXSession() {
    // Check date cheat.
    const cheatedDateStr = window.localStorage?.getItem(XSESSION_DATE_KEY) as string | undefined;
    if (cheatedDateStr) {
        const cheatedDate = new Date(cheatedDateStr);
        if (Number.isNaN(cheatedDate.getTime())) {
            logAttention('[CHEAT WAS NOT ACTIVATED] The xsession date is invalid.');
            return;
        }
        setServerDate(cheatedDate.getTime());
        logAttention(`[CHEAT IS ACTIVATED] The xsession date is ${cheatedDate.toISOString()}`);
    }
}

const successStyle = 'padding: 5px 8px; background-color: #007000; color: #fff; border: 1px solid #6cba6c;';
const informationalStyle = 'padding: 5px 8px; background-color: #0000cc; color: #dddddd; border: 1px solid #999;';
const warningStyle =
    'padding: 5px 8px; background-color: #ffb300; color: #222; border: 1px solid #888; font-weight: bold;';
const errorStyle = 'padding: 5px 8px; background-color: #bb0000; color: #ddd; border: 1px solid #888;';
const attentionStyle =
    'padding: 10px 13px; margin: 10px 0; background-color: #ab004a; color: #fff; border: 1px solid #888; font-weight: bold;';

/** Console log a stylized success message. */
export function logSuccess(firstArg: any, ...args: any[]): void {
    console.log(`%c${firstArg}`, successStyle, ...args);
}

/** Console log a stylized informational message. */
export function logInfo(firstArg: any, ...args: any[]): void {
    console.log(`%c${firstArg}`, informationalStyle, ...args);
}

/** Console log a stylized warning message. */
export function logWarning(firstArg: any, ...args: any[]): void {
    console.log(`%c${firstArg}`, warningStyle, ...args);
}

/** Console log a stylized error message. */
export function logError(firstArg: any, ...args: any[]): void {
    console.log(`%c${firstArg}`, errorStyle, ...args);
}

/** Console log a stylized attention message. The purpose is to draw a lot of attention. */
export function logAttention(firstArg: any, ...args: any[]): void {
    console.log(`%c${firstArg}`, attentionStyle, ...args);
}